/**
 * Progress bar for onboarding flows (create wallet and restore).
 *
 * Shows a thin animated progress bar with step indicator text below.
 * Uses the shared Progress primitive from the design system.
 *
 * Tier 1 - core onboarding UI.
 *
 * @module OnboardingProgressBar
 */

import { Progress } from '@/components/ui/progress';

/** Props for OnboardingProgressBar. */
interface OnboardingProgressBarProps {
  /** Current step number (1-based). */
  readonly currentStep: number;
  /** Total number of steps. */
  readonly totalSteps: number;
  /** Human-readable name for the current step. */
  readonly stepName: string;
}

/**
 * Compact progress bar for multi-step onboarding flows.
 *
 * Renders a thin horizontal bar with "Step X of Y" and step name below.
 * Progress animates between steps using duration-normal token.
 *
 * Tier 1 - core onboarding UI.
 */
export function OnboardingProgressBar({
  currentStep,
  totalSteps,
  stepName,
}: OnboardingProgressBarProps): React.ReactElement {
  const percentage = Math.round((currentStep / totalSteps) * 100);

  return (
    <div className="mb-6 w-full">
      <Progress
        value={percentage}
        className="h-1.5 transition-all duration-200"
      />
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>Step {currentStep} of {totalSteps}</span>
        <span>{stepName}</span>
      </div>
    </div>
  );
}
